import React, {FC, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {FormModelType, useForm} from 'reactjs-use-form'

import DoneIcon from '@mui/icons-material/Done'
import {Alert, Box, Theme, Typography, useTheme} from '@mui/material'
import {PGButton} from '@progynyglobal/pg-ui-library'
import {useSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'

import {numericRegex, PasswordField} from '@progyny/components/PasswordField'
import {useChangePasswordMutation} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {SubpageOneColGrid} from '@progyny/pages/Profile/fragments/grid'
import {useMeStore} from '@progyny/store/meStore'

const useStyles = makeStyles()((theme: Theme) => ({
  label: {
    marginBottom: 16,
  },
  textField: {
    width: '100%',
    maxWidth: 280,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
  button: {
    margin: `${theme.spacing(4)} 0`,
    display: 'block',
  },
  alertBox: {
    marginTop: theme.spacing(2),
  },
  alertIcon: {
    paddingTop: theme.spacing(1.5),
  },
}))

const Security: FC = () => {
  const {classes} = useStyles()
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const [formHasErrors, setFormHasErrors] = useState(false)
  const [changePasswordMutation, {loading}] = useChangePasswordMutation()
  const getErrorMessage = useErrorCodes()
  const theme = useTheme()

  const user = useMeStore((s) => s.user)

  const formModel: FormModelType = {
    currentPassword: {
      value: '',
      required: true,
    },
    password: {
      value: '',
      required: true,
      validator: (password) => {
        if (password.length < 12) {
          return t('profile_security_password_too_short')
        } else if (numericRegex.test(password)) {
          return t('profile_security_password_numeric_only')
        } else return ''
      },
    },
    confirmPassword: {
      value: '',
      required: true,
      validator: (confirmPassword, values) => {
        if (confirmPassword !== values?.password) {
          return t('profile_security_passwords_no_match')
        } else return ''
      },
    },
  }

  const {
    values,
    errors,
    isDisabled,
    isSubmitted,
    handleOnSubmit,
    handleOnChange,
  } = useForm(formModel, handleSubmit)

  function setError(message: string) {
    setFormHasErrors(true)
    enqueueSnackbar({
      message: getErrorMessage(message),
      variant: 'error',
    })
  }

  function handleSubmit() {
    changePasswordMutation({
      variables: {
        newPassword: values.password,
        currentPassword: values.currentPassword!,
      },
    })
      .then((res) => {
        // TODO: refactor
        const code = res.data?.changePassword?.code
        const message = res.data?.changePassword?.message!
        if (code === 403) {
          setError(message)
        } else {
          setFormHasErrors(false)
          enqueueSnackbar({
            message: t('profile_security_password_updated'),
            variant: 'success',
          })
        }
      })
      .catch(({message}) => {
        setError(message)
      })
  }

  return (
    <Box mt={{xs: '80px', md: '100px'}} paddingX={{xs: 2, md: 3}}>
      <SubpageOneColGrid title={t('profile_security')}>
        <Typography
          mt={theme.spacing(6)}
          mb={theme.spacing(4)}
          fontWeight={500}
        >
          <Trans i18nKey='profile_security_password_details'>
            Password details
          </Trans>
        </Typography>
        <form onSubmit={handleOnSubmit}>
          <PasswordField
            showStrength
            onChange={handleOnChange}
            errors={errors}
            values={{
              currentPassword: values.currentPassword,
              password: values.password,
              confirmPassword: values.confirmPassword,
            }}
            label={t('profile_security_new_password')}
          />

          {user?.isManager && (
            <Alert
              severity='info'
              classes={{
                root: classes.alertBox,
                icon: classes.alertIcon,
              }}
            >
              <Typography variant={'caption'}>
                {t('profile_security_dashboard_notice')}
              </Typography>
            </Alert>
          )}
          <PGButton
            type='submit'
            loading={loading}
            size='large'
            disabled={isDisabled || (isSubmitted && !formHasErrors)}
            endIcon={<DoneIcon />}
            label={t('common_save')}
          />
        </form>
      </SubpageOneColGrid>
    </Box>
  )
}

export default Security
